import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Megaphone } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatDate, relativeTime } from '@/lib/format';

export interface MyAnnouncement {
  id: string;
  title: string;
  message: string;
  link: string | null;
  priority: 'normal' | 'important';
  createdAt: string;
  expiresAt: string | null;
  authorName: string;
  readAt: string | null;
  task: { id: string; status: string | null; dueAt: string | null } | null;
}

/** Dashboard paths that show announcement cards (every staff home and the representative portal home). */
export const ANNOUNCEMENT_PATHS = ['/', '/workspace', '/management', '/supervisor', '/executive', '/payment-processing', '/finance', '/services', '/services/banker', '/rep'];

/** Announcements at the top of the home dashboard until the person clicks "Got it". */
export function AnnouncementCards({ className, compact }: { className?: string; compact?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['announcements', 'mine'], queryFn: () => api.get<{ items: MyAnnouncement[] }>('/api/announcements/mine'), refetchInterval: 120_000 });
  const read = useMutation({ mutationFn: (id: string) => api.post(`/api/announcements/${id}/read`) });
  const ack = useMutation({ mutationFn: (id: string) => api.post(`/api/announcements/${id}/acknowledge`), onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }) });
  const items = q.data?.items ?? [];
  useEffect(() => {
    for (const a of items) if (!a.readAt) read.mutate(a.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((a) => a.id).join()]);
  if (!items.length) return null;
  return (
    <div className={clsx('space-y-3', className)} aria-label="Announcements">
      {items.map((a) => {
        const external = a.link?.startsWith('http');
        return (
          <section key={a.id} className={clsx('rounded-xl border p-4', a.priority === 'important' ? 'border-warning/40 bg-warning-soft' : 'border-primary/30 bg-primary-soft')}>
            <div className={clsx('flex gap-3', compact ? 'flex-col' : 'flex-col sm:flex-row sm:items-start')}>
              <Megaphone className={clsx('size-5 shrink-0', a.priority === 'important' ? 'text-warning' : 'text-primary')} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">
                  {a.priority === 'important' && <span className="mr-1.5 rounded bg-warning px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">Important</span>}
                  {a.title}
                </p>
                <p className="mt-1 whitespace-pre-line text-sm">{a.message}</p>
                <p className="mt-1 text-xs text-muted">
                  From {a.authorName} · {relativeTime(a.createdAt)}
                  {a.task ? ` · a task was added to your list${a.task.dueAt ? ` (due ${formatDate(a.task.dueAt)})` : ''}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {a.link &&
                  (external ? (
                    <a href={a.link} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center rounded-lg border border-border bg-surface px-3 text-sm font-medium hover:bg-surface-2">
                      Open
                    </a>
                  ) : (
                    <Link to={a.link} className="inline-flex h-9 items-center rounded-lg border border-border bg-surface px-3 text-sm font-medium hover:bg-surface-2">
                      Open
                    </Link>
                  ))}
                {a.task && (
                  <Link to={compact ? '/rep/tasks' : `/tasks/${a.task.id}`} className="inline-flex h-9 items-center rounded-lg border border-border bg-surface px-3 text-sm font-medium hover:bg-surface-2">
                    Task
                  </Link>
                )}
                <button type="button" disabled={ack.isPending} onClick={() => ack.mutate(a.id)} className="inline-flex h-9 items-center rounded-lg bg-primary px-3 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60">
                  Got it
                </button>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
